/**
 * ConfirmDialog
 *
 * Reusable confirmation dialog that replaces native window.confirm().
 * Provides an async-await API via the useConfirm() hook:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title: '...', message: '...', dangerous: true });
 *   if (!ok) return;
 *
 * Mount <ConfirmProvider> once near the app root (already done in App.tsx).
 */

import { Fragment, createContext, useCallback, useContext, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  /** Render the confirm button in red and use a warning icon. */
  dangerous?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return ctx;
}

interface PendingState {
  options: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [pending, setPending] = useState<PendingState | null>(null);
  const pendingRef = useRef<PendingState | null>(null);

  const confirm = useCallback<ConfirmFn>(options => {
    return new Promise<boolean>(resolve => {
      const state: PendingState = { options, resolve };
      pendingRef.current = state;
      setPending(state);
    });
  }, []);

  const handleClose = useCallback((value: boolean) => {
    const current = pendingRef.current;
    if (current) {
      current.resolve(value);
      pendingRef.current = null;
    }
    setPending(null);
  }, []);

  const isOpen = pending !== null;
  const opts = pending?.options;
  const dangerous = opts?.dangerous ?? false;
  const title = opts?.title ?? t('common.confirm', 'Confirm');
  const confirmText = opts?.confirmText ?? t('common.confirm', 'Confirm');
  const cancelText = opts?.cancelText ?? t('common.cancel', 'Cancel');

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => handleClose(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  dir={isRtl ? 'rtl' : 'ltr'}
                  className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-start align-middle shadow-xl transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                        dangerous ? 'bg-red-100' : 'bg-brand-50'
                      }`}
                    >
                      {dangerous ? (
                        <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
                      ) : (
                        <QuestionMarkCircleIcon className="h-5 w-5 text-brand-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        {title}
                      </Dialog.Title>
                      {opts?.message && (
                        <p className="mt-2 text-sm text-gray-600 whitespace-pre-line">
                          {opts.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <button
                      type="button"
                      autoFocus
                      onClick={() => handleClose(false)}
                      className="inline-flex justify-center items-center rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                    >
                      {cancelText}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleClose(true)}
                      className={`inline-flex justify-center items-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                        dangerous
                          ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                          : 'bg-brand-700 hover:bg-brand-800 focus:ring-brand-500'
                      }`}
                    >
                      {confirmText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </ConfirmContext.Provider>
  );
}

export default ConfirmProvider;
